import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const preAdjudicationReasonKeys = {
  all: ['preAdjudicationReasons'],
  lists: () => [...preAdjudicationReasonKeys.all, 'list'],
  details: () => [...preAdjudicationReasonKeys.all, 'detail'],
  detail: (id) => [...preAdjudicationReasonKeys.details(), id],
};

/**
 * Hook para obtener todas las razones de pre-adjudicación
 */
export const usePreAdjudicationReasons = () => {
  return useQuery({
    queryKey: preAdjudicationReasonKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('pre_adjudication_reasons');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener una razón de pre-adjudicación específica
 * @param {string} id - UUID de la razón de pre-adjudicación
 * @param {Object} options - Opciones adicionales para la query
 */
export const usePreAdjudicationReason = (id, options = {}) => {
  return useQuery({
    queryKey: preAdjudicationReasonKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`pre_adjudication_reasons/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear una nueva razón de pre-adjudicación
 */
export const useCreatePreAdjudicationReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (preAdjudicationReasonData) => {
      const response = await apiClient.post('pre_adjudication_reasons', preAdjudicationReasonData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preAdjudicationReasonKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating pre-adjudication reason:', error);
    },
  });
};

/**
 * Hook para actualizar una razón de pre-adjudicación existente
 */
export const useUpdatePreAdjudicationReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`pre_adjudication_reasons/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(preAdjudicationReasonKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: preAdjudicationReasonKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating pre-adjudication reason:', error);
    },
  });
};

/**
 * Hook para eliminar una razón de pre-adjudicación
 */
export const useDeletePreAdjudicationReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`pre_adjudication_reasons/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preAdjudicationReasonKeys.lists() });
    },
    onError: (error) => {
      console.error('Error deleting pre-adjudication reason:', error);
    },
  });
};
